import { useCallback, useMemo, useState } from 'react';
import { DropdownProps, MenuProps } from 'antd';
import { ToolbarButtonProps } from './ToolbarButton';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from 'lexical';
import { $createHeadingNode, QuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import { $setBlocksType } from '@lexical/selection';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';

import {
  CodeOutlined,
  CommentOutlined,
  MinusSquareOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ToolbarDropdownButton, renderMenuItem } from './ToolbarDropdownButton';
import styles from '../RichTextEditor.module.scss';
import { useTranslation } from 'react-i18next';

export type BlockType =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bullet'
  | 'number'
  | 'quote'
  | 'code';

export interface ToolbarBlockTypeDropdownButtonProps
  extends ToolbarButtonProps,
    Pick<DropdownProps, 'trigger' | 'overlay'> {
  selected?: BlockType;
}

export function HeadingIcon({ level }: { level: 1 | 2 | 3 }) {
  return <span className={styles.headingIcon}>{`H${level}`}</span>;
}

export const BLOCK_TYPE_META_BASE: Record<
  BlockType,
  { icon: React.ReactNode }
> = {
  paragraph: { icon: <MinusSquareOutlined /> },
  h1: { icon: <HeadingIcon level={1} /> },
  h2: { icon: <HeadingIcon level={2} /> },
  h3: { icon: <HeadingIcon level={3} /> },
  bullet: { icon: <UnorderedListOutlined /> },
  number: { icon: <OrderedListOutlined /> },
  quote: { icon: <CommentOutlined /> },
  code: { icon: <CodeOutlined /> },
};

function useLocalizedBlockMeta() {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = Object.entries(BLOCK_TYPE_META_BASE);

    return Object.fromEntries(
      entries.map(([key, val]) => [
        key,
        {
          icon: val.icon,
          label: t(`editor.block.${key}`),
        },
      ]),
    ) as Record<BlockType, { icon: React.ReactNode; label: string }>;
  }, [t]);
}

export function useBlockTypeMenu(editor: LexicalEditor) {
  const [selected, setSelected] = useState<BlockType>('paragraph');
  const meta = useLocalizedBlockMeta();

  const selectBlock = useCallback(
    (type: BlockType, createFn?: () => any) => {
      setSelected(type);

      if (createFn) {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, createFn);
          }
        });
      }
    },
    [editor],
  );

  const items = useMemo<MenuProps['items']>(() => {
    return [
      {
        key: 'paragraph',
        label: renderMenuItem(meta.paragraph.icon, meta.paragraph.label),
        onClick: () => selectBlock('paragraph', () => $createParagraphNode()),
      },
      {
        key: 'h1',
        label: renderMenuItem(meta.h1.icon, meta.h1.label),
        onClick: () => selectBlock('h1', () => $createHeadingNode('h1')),
      },
      {
        key: 'h2',
        label: renderMenuItem(meta.h2.icon, meta.h2.label),
        onClick: () => selectBlock('h2', () => $createHeadingNode('h2')),
      },
      {
        key: 'h3',
        label: renderMenuItem(meta.h3.icon, meta.h3.label),
        onClick: () => selectBlock('h3', () => $createHeadingNode('h3')),
      },
      {
        key: 'bullet',
        label: renderMenuItem(meta.bullet.icon, meta.bullet.label),
        onClick: () => {
          setSelected('bullet');
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        },
      },
      {
        key: 'number',
        label: renderMenuItem(meta.number.icon, meta.number.label),
        onClick: () => {
          setSelected('number');
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        },
      },
      {
        key: 'quote',
        label: renderMenuItem(meta.quote.icon, meta.quote.label),
        onClick: () => {
          setSelected('quote');
          editor.update(() => {
            const sel = $getSelection();
            if ($isRangeSelection(sel)) {
              $setBlocksType(sel, () => new QuoteNode());
            }
          });
        },
      },
      false && {
        key: 'code',
        label: renderMenuItem(meta.code.icon, meta.code.label),
        onClick: () => selectBlock('code', () => $createCodeNode()),
      },
    ].filter((x) => !!x) as MenuProps['items'];
  }, [editor, meta, selectBlock]);

  return {
    blockTypeMenu: items,
    selectedBlockType: selected,
    meta,
  };
}

export function ToolbarBlockTypeDropdownButton(
  props: ToolbarBlockTypeDropdownButtonProps,
) {
  const { selected, ...dropDownProps } = props;

  const meta = useLocalizedBlockMeta();
  const blockMeta = selected ? meta[selected] : undefined;

  return <ToolbarDropdownButton {...dropDownProps} selectedItem={blockMeta} />;
}
